import { useState, useEffect, useCallback } from 'react';
import {
  Headphones, Plus, ChevronLeft, ChevronRight, Clock, CheckCircle2, AlertTriangle,
  MessageSquare, X, Search, Send, User, FileText, Check, ShieldAlert,
  Columns, List
} from 'lucide-react';
import { api } from '../../api/client';
import { Modal } from '../../components/ui/Modal';
import { KanbanBoard } from '../../components/ui/KanbanBoard';
import '../../components/ui/datatable.css';

interface ChamadoItem {
  id: string;
  numero: number;
  titulo: string;
  descricao: string;
  categoria: string;
  prioridade: string;
  status: string;
  criadoEm: string;
  abertoPor: { id: string; nome: string; email: string; perfil: string } | null;
  escola: { id: string; nome: string } | null;
  atribuidoPara: { id: string; nome: string } | null;
  _count: { respostas: number };
}

const STATUS_STYLES: Record<string, { color: string; bg: string }> = {
  ABERTO: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.08)' },
  EM_ANDAMENTO: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)' },
  AGUARDANDO_USUARIO: { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.08)' },
  RESOLVIDO: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.08)' },
  FECHADO: { color: '#64748b', bg: 'var(--bg-muted, #f1f5f9)' },
};

const PRIORIDADE_STYLES: Record<string, { color: string; bg: string }> = {
  BAIXA: { color: '#64748b', bg: 'var(--bg-muted, #f1f5f9)' },
  MEDIA: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.08)' },
  ALTA: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)' },
  URGENTE: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)' },
};

const KANBAN_COLUMNS = [
  { id: 'ABERTO', label: 'Aberto', color: '#3b82f6' },
  { id: 'EM_ANDAMENTO', label: 'Em Andamento', color: '#f59e0b' },
  { id: 'AGUARDANDO_USUARIO', label: 'Aguardando Usuário', color: '#8b5cf6' },
  { id: 'RESOLVIDO', label: 'Resolvido', color: '#10b981' },
  { id: 'FECHADO', label: 'Fechado', color: '#64748b' },
];

export function SuporteChamadosPage() {
  const [items, setItems] = useState<ChamadoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'lista' | 'kanban'>('lista');

  // Filtros
  const [busca, setBusca] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('');
  const [prioridadeFilter, setPrioridadeFilter] = useState('');

  // Estatísticas em tempo real
  const [counts, setCounts] = useState<Record<string, number>>({});

  // Estados dos Modais
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedChamadoId, setSelectedChamadoId] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyContent, setReplyContent] = useState('');

  // Estados de submissão
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);

  const limite = 20;

  const fetchData = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    const params: Record<string, string> = {
      page: viewMode === 'kanban' ? '1' : String(page),
      limite: viewMode === 'kanban' ? '1000' : String(limite),
    };
    if (busca) params.busca = busca;
    if (statusFilter) params.status = statusFilter;
    if (categoriaFilter) params.categoria = categoriaFilter;
    if (prioridadeFilter) params.prioridade = prioridadeFilter;

    api.get('/suporte/chamados', { params })
      .then(r => {
        const data = r.data || {};
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, [page, busca, statusFilter, categoriaFilter, prioridadeFilter, viewMode]);

  const handleDropKanban = async (itemId: string, targetStatus: string) => {
    // Optimistic update
    setItems(prev => prev.map(c => c.id === itemId ? { ...c, status: targetStatus } : c));
    try {
      await api.patch(`/suporte/chamados/${itemId}`, {
        status: targetStatus,
      });
      fetchData(true);
      fetchCounts();
    } catch (err) {
      console.error(err);
      fetchData();
    }
  };

  const fetchCounts = useCallback(() => {
    api.get('/suporte/chamados/contagem')
      .then(r => {
        setCounts(r.data || {});
      })
      .catch(console.error);
  }, []);

  const fetchChamadoDetail = useCallback((id: string) => {
    setDetailLoading(true);
    api.get(`/suporte/chamados/${id}`)
      .then(r => {
        setDetailData(r.data);
      })
      .catch(console.error)
      .finally(() => setDetailLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const totalPages = Math.ceil(total / limite) || 1;

  const handleOpenDetail = (id: string) => {
    setSelectedChamadoId(id);
    setDetailData(null);
    setShowDetailModal(true);
    fetchChamadoDetail(id);
  };

  const handleCriar = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreateSubmitting(true);
    const formData = new FormData(e.currentTarget);

    try {
      await api.post('/suporte/chamados', {
        titulo: formData.get('titulo'),
        descricao: formData.get('descricao'),
        categoria: formData.get('categoria'),
        prioridade: formData.get('prioridade'),
      });
      setShowCreateModal(false);
      setPage(1);
      fetchData();
      fetchCounts();
    } catch (err) {
      console.error(err);
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedChamadoId || !replyContent.trim()) return;

    setReplySubmitting(true);
    try {
      await api.post(`/suporte/chamados/${selectedChamadoId}/respostas`, {
        conteudo: replyContent,
      });
      setReplyContent('');
      fetchChamadoDetail(selectedChamadoId);
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleUpdateChamado = async (field: 'status' | 'prioridade', value: string) => {
    if (!selectedChamadoId) return;
    try {
      await api.patch(`/suporte/chamados/${selectedChamadoId}`, {
        [field]: value,
      });
      fetchChamadoDetail(selectedChamadoId);
      fetchData();
      fetchCounts();
    } catch (err) {
      console.error(err);
    }
  };



  const thStyle: React.CSSProperties = {
    padding: '10px 14px', fontSize: '0.75rem', fontWeight: 600,
    color: 'var(--text-muted)', textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: '1px solid var(--border-color)',
    textAlign: 'left',
  };

  const tdStyle: React.CSSProperties = {
    padding: '12px 14px', fontSize: '0.85rem', color: 'var(--text-main)',
    borderBottom: '1px solid var(--border-color, #f1f5f9)',
  };

  return (
    <div className="admin-page animate-fadeIn chamado-page-compact" style={{ marginTop: -22, height: 'calc(100vh - 116px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '24px 28px' }}>
      
      {/* ═══ HERO BANNER PREMIUM ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #2e1065 0%, #3b0764 40%, #581c87 100%)',
        borderRadius: 18, padding: 'clamp(12px, 1.4vw, 18px)', marginBottom: 12, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                <Headphones size={16} style={{ color: '#c084fc' }} />
                <span style={{ color: 'rgba(255,255,255,.45)', fontSize: '.66rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>Suporte</span>
              </div>
              <h1 style={{ color: 'white', fontSize: 'clamp(1.05rem, 1.6vw, 1.28rem)', fontWeight: 800, margin: 0 }}>Chamados de Suporte</h1>
              <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.75rem', marginTop: 2 }}>
                Central de atendimento ao cliente, dúvidas técnicas, problemas de acesso e incidentes
              </p>
            </div>
            
            <button onClick={() => setShowCreateModal(true)} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9,
              background: '#c084fc', color: '#2e1065', fontWeight: 700, fontSize: '.8rem', border: 'none', cursor: 'pointer',
            }}>
              <Plus size={14} /> Novo Chamado
            </button>
          </div>

          {/* Stats Grid inside the Hero */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginTop: 12 }}>
            {[
              { label: 'Todos os Chamados', value: total, color: '#c084fc', icon: Headphones },
              { label: 'Abertos', value: counts.ABERTO || 0, color: '#60a5fa', icon: Clock },
              { label: 'Em Andamento', value: counts.EM_ANDAMENTO || 0, color: '#fbbf24', icon: AlertTriangle },
              { label: 'Resolvidos / Fechados', value: (counts.RESOLVIDO || 0) + (counts.FECHADO || 0), color: '#34d399', icon: CheckCircle2 },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', padding: '6px 10px', borderRadius: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}15`, color: s.color, flexShrink: 0 }}>
                  <s.icon size={16} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 'clamp(.9rem, 1.3vw, 1.15rem)', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '.64rem', color: 'rgba(255,255,255,.6)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FILTER BAR & TABLE CONTAINER */}
      <div style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 14, overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        
        {/* Filters bar */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-main, #f8fafc)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                value={busca}
                onChange={(e) => { setBusca(e.target.value); setPage(1); }}
                placeholder="Buscar por título ou descrição..."
                style={{ height: 34, width: 260, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px 0 30px', fontSize: '.82rem', background: 'var(--bg-card)', color: 'var(--text-main)' }}
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              style={{ height: 34, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px', fontSize: '.82rem', background: 'var(--bg-card)', color: 'var(--text-main)', cursor: 'pointer' }}
            >
              <option value="">Todos os status</option>
              <option value="ABERTO">Aberto</option>
              <option value="EM_ANDAMENTO">Em Andamento</option>
              <option value="AGUARDANDO_USUARIO">Aguardando Usuário</option>
              <option value="RESOLVIDO">Resolvido</option>
              <option value="FECHADO">Fechado</option>
            </select>

            <select
              value={categoriaFilter}
              onChange={(e) => { setCategoriaFilter(e.target.value); setPage(1); }}
              style={{ height: 34, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px', fontSize: '.82rem', background: 'var(--bg-card)', color: 'var(--text-main)', cursor: 'pointer' }}
            >
              <option value="">Todas as categorias</option>
              <option value="SISTEMA">Sistema</option>
              <option value="DISPOSITIVO">Dispositivo</option>
              <option value="ACESSO">Acesso</option>
              <option value="CADASTRO">Cadastro</option>
              <option value="IMPLANTACAO">Implantação</option>
              <option value="FINANCEIRO">Financeiro</option>
              <option value="OUTRO">Outro</option>
            </select>

            <select
              value={prioridadeFilter}
              onChange={(e) => { setPrioridadeFilter(e.target.value); setPage(1); }}
              style={{ height: 34, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px', fontSize: '.82rem', background: 'var(--bg-card)', color: 'var(--text-main)', cursor: 'pointer' }}
            >
              <option value="">Todas as prioridades</option>
              <option value="BAIXA">Baixa</option>
              <option value="MEDIA">Média</option>
              <option value="ALTA">Alta</option>
              <option value="URGENTE">Urgente</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{total} registro(s)</span>
            
            {/* View Mode Toggle */}
            <div style={{ display: 'flex', background: 'var(--bg-main, #f1f5f9)', padding: 3, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)' }}>
              <button
                type="button"
                onClick={() => setViewMode('lista')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6,
                  border: 'none', background: viewMode === 'lista' ? 'var(--bg-card, white)' : 'transparent',
                  color: viewMode === 'lista' ? 'var(--text-main, #111827)' : 'var(--text-muted, #64748b)',
                  fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                  boxShadow: viewMode === 'lista' ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                  transition: 'all 0.15s'
                }}
              >
                <List size={13} />
                Lista
              </button>
              <button
                type="button"
                onClick={() => setViewMode('kanban')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6,
                  border: 'none', background: viewMode === 'kanban' ? 'var(--bg-card, white)' : 'transparent',
                  color: viewMode === 'kanban' ? 'var(--text-main, #111827)' : 'var(--text-muted, #64748b)',
                  fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                  boxShadow: viewMode === 'kanban' ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                  transition: 'all 0.15s'
                }}
              >
                <Columns size={13} />
                Kanban
              </button>
            </div>
          </div>
        </div>

        {/* Content table scroll */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {viewMode === 'kanban' ? (
            <div style={{ flex: 1, padding: 12, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <KanbanBoard<ChamadoItem>
                columns={KANBAN_COLUMNS}
                items={items}
                getColumnId={(c) => c.status}
                onDrop={handleDropKanban}
                onCardClick={(c) => handleOpenDetail(c.id)}
                isLoading={loading}
                loadingText="Carregando chamados..."
                hideNav
                renderCard={(c) => {
                  const ps = PRIORIDADE_STYLES[c.prioridade] || PRIORIDADE_STYLES.MEDIA;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.66rem', fontWeight: 700, color: 'var(--text-muted)' }}>
                          #{c.numero}
                        </span>
                        <span style={{
                          fontSize: '0.64rem', fontWeight: 800, padding: '1px 6px', borderRadius: 4,
                          background: 'rgba(99, 102, 241, 0.08)', color: '#6366f1'
                        }}>
                          {c.categoria}
                        </span>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-main)', lineHeight: 1.3 }}>
                        {c.titulo}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                        <span style={{
                          fontSize: '0.58rem', fontWeight: 800, padding: '1px 5px', borderRadius: 4,
                          background: ps.bg, color: ps.color
                        }}>
                          {c.prioridade}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {c._count.respostas > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '0.64rem', color: 'var(--text-muted)' }}>
                              <MessageSquare size={10} /> {c._count.respostas}
                            </span>
                          )}
                          <div
                            title={`Aberto por: ${c.abertoPor?.nome}`}
                            style={{
                              width: 18, height: 18, borderRadius: '50%',
                              background: 'linear-gradient(135deg, #10b981, #059669)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.52rem', color: '#fff', fontWeight: 800,
                              border: '1.5px solid var(--bg-card, #fff)'
                            }}
                          >
                            {c.abertoPor?.nome ? c.abertoPor.nome.charAt(0).toUpperCase() : '?'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
            </div>
          ) : (
            <>
              <div style={{ flex: 1, overflowY: 'auto' }} className="custom-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-main, #f8fafc)', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Prioridade</th>
                      <th style={thStyle}>Título</th>
                      <th style={thStyle}>Categoria</th>
                      <th style={thStyle}>Aberto por</th>
                      <th style={thStyle}>Respostas</th>
                      <th style={thStyle}>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</td></tr>
                    ) : items.length === 0 ? (
                      <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                        Nenhum chamado encontrado. Clique em "Novo Chamado" para criar.
                      </td></tr>
                    ) : items.map(c => {
                      const ss = STATUS_STYLES[c.status] || STATUS_STYLES.ABERTO;
                      const ps = PRIORIDADE_STYLES[c.prioridade] || PRIORIDADE_STYLES.MEDIA;
                      return (
                        <tr
                          key={c.id}
                          onClick={() => handleOpenDetail(c.id)}
                          style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover, #f8fafc)')}
                          onMouseLeave={e => (e.currentTarget.style.background = '')}
                        >
                          <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600, color: 'var(--text-muted)' }}>
                            #{c.numero}
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600,
                              background: ss.bg, color: ss.color,
                            }}>
                              {c.status === 'ABERTO' && <Clock size={11} />}
                              {c.status === 'RESOLVIDO' && <CheckCircle2 size={11} />}
                              {c.status === 'EM_ANDAMENTO' && <AlertTriangle size={11} />}
                              {c.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={tdStyle}>
                            <span style={{
                              padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600,
                              background: ps.bg, color: ps.color,
                            }}>
                              {c.prioridade}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 600, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.titulo}
                          </td>
                          <td style={{ ...tdStyle, fontSize: '0.8rem' }}>{c.categoria}</td>
                          <td style={{ ...tdStyle, fontSize: '0.8rem' }}>{c.abertoPor?.nome || '-'}</td>
                          <td style={tdStyle}>
                            {c._count.respostas > 0 ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                <MessageSquare size={13} /> {c._count.respostas}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>0</span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            {new Date(c.criadoEm).toLocaleDateString('pt-BR')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer Pagination */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid var(--border-color)', background: 'var(--bg-main, #f8fafc)' }}>
                <span style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>
                  Página {page} de {totalPages}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    style={{ height: 30, width: 30, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card)', color: 'var(--text-main)', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.45 : 1 }}
                  >
                    <ChevronLeft size={14} style={{ margin: '0 auto' }} />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    style={{ height: 30, width: 30, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card)', color: 'var(--text-main)', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.45 : 1 }}
                  >
                    <ChevronRight size={14} style={{ margin: '0 auto' }} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal Novo Chamado */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title=""
        size="default"
        contentClassName="umu-wizard-modal"
        footer={
          <div style={{ display: 'flex', gap: 10, width: '100%', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="btn-cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="chamado-form"
              disabled={createSubmitting}
              className="btn-save"
              style={{ background: 'hsl(var(--primary))' }}
            >
              {createSubmitting ? 'Criando...' : 'Criar Chamado'}
            </button>
          </div>
        }
      >
        <form id="chamado-form" onSubmit={handleCriar} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 40%, #818cf8 100%)',
            borderRadius: '12px 12px 0 0', padding: '20px 24px 16px', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
                Novo Chamado
              </h2>
              <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.78rem', margin: 0 }}>
                Abra um novo chamado de suporte na plataforma.
              </p>
            </div>
          </div>

          {/* Body Fields */}
          <div style={{
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto'
          }}>
            <div className="form-group">
              <label className="form-label">Título *</label>
              <input name="titulo" required minLength={5} className="form-input" placeholder="Descreva o problema em poucas palavras" />
            </div>
            <div className="form-group">
              <label className="form-label">Descrição *</label>
              <textarea name="descricao" required minLength={10} rows={4}
                className="form-input"
                style={{ resize: 'vertical' }}
                placeholder="Detalhe o problema, incluindo passos para reproduzir"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Categoria</label>
                <select name="categoria" className="form-select">
                  <option value="SISTEMA">Sistema</option>
                  <option value="DISPOSITIVO">Dispositivo</option>
                  <option value="ACESSO">Acesso</option>
                  <option value="CADASTRO">Cadastro</option>
                  <option value="IMPLANTACAO">Implantação</option>
                  <option value="FINANCEIRO">Financeiro</option>
                  <option value="OUTRO">Outro</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Prioridade</label>
                <select name="prioridade" className="form-select">
                  <option value="BAIXA">Baixa</option>
                  <option value="MEDIA">Média</option>
                  <option value="ALTA">Alta</option>
                  <option value="URGENTE">Urgente</option>
                </select>
              </div>
            </div>
          </div>
        </form>
      </Modal>

      {/* Modal Detalhes do Chamado */}
      <Modal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title=""
        size="lg"
        contentClassName="umu-wizard-modal"
        footer={
          detailData && (
            <div style={{ display: 'flex', gap: 12, width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  value={detailData.status}
                  onChange={e => handleUpdateChamado('status', e.target.value)}
                  className="form-select"
                  style={{ fontSize: '0.8rem', padding: '4px 10px', width: 'auto' }}
                >
                  <option value="ABERTO">Aberto</option>
                  <option value="EM_ANDAMENTO">Em Andamento</option>
                  <option value="AGUARDANDO_USUARIO">Aguardando Usuário</option>
                  <option value="RESOLVIDO">Resolvido</option>
                  <option value="FECHADO">Fechado</option>
                </select>

                <select
                  value={detailData.prioridade}
                  onChange={e => handleUpdateChamado('prioridade', e.target.value)}
                  className="form-select"
                  style={{ fontSize: '0.8rem', padding: '4px 10px', width: 'auto' }}
                >
                  <option value="BAIXA">Baixa</option>
                  <option value="MEDIA">Média</option>
                  <option value="ALTA">Alta</option>
                  <option value="URGENTE">Urgente</option>
                </select>
              </div>
              
              <button
                type="button"
                onClick={() => setShowDetailModal(false)}
                className="btn-cancel"
              >
                Fechar
              </button>
            </div>
          )
        }
      >
        {detailLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando detalhes do chamado...</div>
        ) : !detailData ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Erro ao carregar detalhes.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #311042 100%)',
              borderRadius: '12px 12px 0 0', padding: '16px 20px 12px', position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ color: 'white', fontSize: '1.05rem', fontWeight: 800, margin: '0 0 2px' }}>
                    Chamado #{detailData.numero}
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.74rem', margin: 0 }}>
                    {detailData.titulo}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
                    background: STATUS_STYLES[detailData.status]?.bg || 'rgba(255,255,255,0.1)',
                    color: STATUS_STYLES[detailData.status]?.color || 'white'
                  }}>
                    {detailData.status}
                  </span>
                  <span style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
                    background: PRIORIDADE_STYLES[detailData.prioridade]?.bg || 'rgba(255,255,255,0.1)',
                    color: PRIORIDADE_STYLES[detailData.prioridade]?.color || 'white'
                  }}>
                    {detailData.prioridade}
                  </span>
                </div>
              </div>
            </div>

            {/* Body */}
            <div style={{
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              flex: '1 1 auto',
              minHeight: 0,
              maxHeight: 'min(580px, 72vh)',
              overflowY: 'auto'
            }} className="custom-scroll">
              
              {/* Ticket Author / Info */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderBottom: '1px solid var(--border-color)', paddingBottom: 12 }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Aberto Por</div>
                  <strong style={{ fontSize: '0.85rem', color: 'var(--text-main)' }}>{detailData.abertoPor?.nome || '-'}</strong>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{detailData.abertoPor?.email}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Data de Abertura</div>
                  <strong style={{ fontSize: '0.85rem', color: 'var(--text-main)' }}>
                    {new Date(detailData.criadoEm).toLocaleString('pt-BR')}
                  </strong>
                  {detailData.escola && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      Escola: <strong>{detailData.escola.nome}</strong>
                    </div>
                  )}
                </div>
              </div>

              {/* Main Ticket Desc */}
              <div style={{ padding: 16, borderRadius: 10, background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 6 }}>Descrição do Chamado:</div>
                <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--text-main)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {detailData.descricao}
                </p>
              </div>

              {/* Thread list */}
              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: 16 }}>
                <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: 12 }}>
                  Histórico de Respostas ({detailData.respostas?.length || 0})
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 240, overflowY: 'auto', paddingRight: 6 }} className="custom-scroll">
                  {(!detailData.respostas || detailData.respostas.length === 0) ? (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                      Nenhuma resposta registrada ainda.
                    </div>
                  ) : detailData.respostas.map((r: any) => {
                    const isSystemOrAdmin = r.autor?.perfil === 'ADMIN' || r.autor?.perfil === 'MASTER';
                    return (
                      <div key={r.id} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        padding: '10px 14px',
                        borderRadius: 10,
                        background: isSystemOrAdmin ? 'rgba(99, 102, 241, 0.05)' : 'var(--bg-main, #f8fafc)',
                        border: isSystemOrAdmin ? '1px solid rgba(99, 102, 241, 0.15)' : '1px solid var(--border-color)',
                        alignSelf: isSystemOrAdmin ? 'flex-end' : 'flex-start',
                        maxWidth: '90%',
                        minWidth: '40%',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: isSystemOrAdmin ? '#6366f1' : 'var(--text-main)' }}>
                            {r.autor?.nome || 'Usuário'}
                            <span style={{ marginLeft: 6, fontSize: '0.64rem', padding: '1px 6px', borderRadius: 10, background: isSystemOrAdmin ? 'rgba(99, 102, 241, 0.15)' : 'var(--border-color)', color: isSystemOrAdmin ? '#6366f1' : 'var(--text-muted)', fontWeight: 600 }}>
                              {r.autor?.perfil}
                            </span>
                          </span>
                          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                            {new Date(r.criadoEm).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                        </div>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-main)', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                          {r.conteudo}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Reply Form */}
              {detailData.status !== 'FECHADO' && (
                <form onSubmit={handleSendReply} style={{ borderTop: '1px solid var(--border-color)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <label className="form-label">Nova Resposta</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <textarea
                      value={replyContent}
                      onChange={e => setReplyContent(e.target.value)}
                      placeholder="Digite sua resposta aqui..."
                      required
                      rows={2}
                      className="form-input"
                      style={{ resize: 'none', flex: 1 }}
                    />
                    <button
                      type="submit"
                      disabled={replySubmitting || !replyContent.trim()}
                      style={{
                        padding: '0 18px', borderRadius: 8, border: 'none',
                        background: 'hsl(var(--primary))', color: '#fff', fontSize: '0.85rem',
                        fontWeight: 700, cursor: (replySubmitting || !replyContent.trim()) ? 'not-allowed' : 'pointer',
                        opacity: (replySubmitting || !replyContent.trim()) ? 0.7 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 42,
                        alignSelf: 'flex-end',
                      }}
                    >
                      <Send size={14} /> Enviar
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
